import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Waypoint } from 'react-waypoint';
import { Button } from 'reactstrap';
import { useIntl } from 'react-intl';

import {
  setUserSetting,
  updateUserSettings,
} from '../../actions/Auth';
import {
  fetchCollections,
  clearCollectionsList,
  clearSelectedCollections,
  setCollectionsLoading,
  setCollectionsParams,
} from '../../actions/Collections';
import {
  clearFiles,
  fetchFiles,
  setFilesParams,
} from '../../actions/Files';

import CollectionsGrid from '../../components/Collections/Grid';
import CollectionsList from '../../components/Collections/List';
import CollectionAddModal from '../../components/Collections/AddModalV2';
import CollectionsMoveModal from '../../components/Collections/MoveModalV2';
import CollectionsSelectAll from '../../components/Collections/SelectAll';
import Sort from '../../components/Filters/Sort';
import ViewMode from '../../components/Filters/ViewMode';
import Loading from '../../components/Loading';
import CollectionsMiniGrid from '../../mini/components/Collections/Grid';

import { isMini } from '../../helpers/App';
import { getCurrentUser } from '../../helpers/Auth';
import { canDownloadPageItem } from '../../helpers/PageSections';
import {
  getCollectionsView,
  getCollectionAllowedViews,
} from '../../helpers/Collections';

const CollectionResultsSection = ({
  dispatch,
  collection,
  collections,
  collectionsEnd,
  collectionsTotal,
  collectionsLoading,
  collectionMoveModal,
  moveModalCollection,
  selectedCollections,
  filesParams,
  search,
  handleMoveSelectedItems,
  onLoadMore,
  onRefresh,
  displayOnly,
  hideSaveSort,
  renderDisplayAction,
  collectionUrlResolver,
}) => {
  const intl = useIntl();
  const [collectionsExpanded, setCollectionsExpanded] = useState(true);
  const [loadMoreCollections, setLoadMoreCollections] = useState(false);

  const {
    orderby,
    order,
  } = filesParams;

  const savedCollectionsView = getCollectionsView();
  const collectionsView = displayOnly && savedCollectionsView === 'list' ? 'grid' : savedCollectionsView;
  const selectableCollections = displayOnly
    ? collections.filter(canDownloadPageItem) : collections;

  let moveModalCollections = [];
  if (selectedCollections.length > 0) {
    moveModalCollections = selectedCollections;
  } else if (moveModalCollection) {
    moveModalCollections = [moveModalCollection];
  }

  const getLoadedCollectionsLabel = () => {
    if (collectionsTotal === 0) {
      return null;
    }

    return intl.formatMessage({ defaultMessage: '({collections} of {collectionsTotal})' }, {
      collections: collections.length, collectionsTotal,
    });
  };

  const loadCollections = () => {
    if (onLoadMore) {
      onLoadMore();
    } else {
      dispatch(fetchCollections());
    }
  };

  const handleLoadMoreCollections = () => {
    setLoadMoreCollections(true);
    loadCollections();
  };

  const handleSwitchCollectionsExpanded = (e) => {
    e.preventDefault();
    setCollectionsExpanded(!collectionsExpanded);
  };

  const setOrder = (newOrderby, newOrder) => {
    if (onRefresh) {
      dispatch(setFilesParams({ orderby: newOrderby, order: newOrder }));
      onRefresh();
      return;
    }
    dispatch(clearFiles());
    dispatch(setFilesParams({
      orderby: newOrderby,
      order: newOrder,
      page: 0,
    }));
    dispatch(fetchFiles());
    dispatch(setCollectionsParams({ page: 0 }));
    dispatch(clearCollectionsList());
    dispatch(clearSelectedCollections());
    dispatch(fetchCollections());
  };

  const setCollectionsView = (view) => {
    const profile = getCurrentUser();
    dispatch(clearCollectionsList());
    dispatch(setCollectionsLoading(true));
    dispatch(setUserSetting({ collections_view: view }));
    if (profile.uuid) {
      dispatch(updateUserSettings(profile.uuid, { collections_view: view })).then(() => {
        if (onRefresh) {
          onRefresh();
          return;
        }
        dispatch(setCollectionsParams({ page: 0 }));
        dispatch(fetchCollections());
      });
    } else {
      if (onRefresh) {
        onRefresh();
        return;
      }
      dispatch(setCollectionsParams({ page: 0 }));
      dispatch(fetchCollections());
    }
  };

  return (
    <>
      <div className="collection-tools">
        <div className="list-switch">
          <a
            href="#collections-switch"
            onClick={handleSwitchCollectionsExpanded}
          >
            {intl.formatMessage({ defaultMessage: 'Collections {label}' }, { label: getLoadedCollectionsLabel() })}{' '}
            {collectionsExpanded ? (
              <i className="far fa-chevron-down" />
            ) : (
              <i className="far fa-chevron-right" />
            )}
          </a>
        </div>
        <div className="collection-tools-actions">
          <div className="collection-tools-actions-buttons">
            <Sort
              orderby={orderby}
              order={order}
              search={search}
              setOrder={setOrder}
              hideSave={hideSaveSort}
              collection={collection}
              size="sm"
            />
            <ViewMode
              view={collectionsView}
              setView={setCollectionsView}
              size="sm"
              allowedViews={getCollectionAllowedViews().filter(v => !displayOnly || v !== 'list')}
            />
          </div>
          {collectionsExpanded && (
            <CollectionsSelectAll
              collections={selectableCollections}
              {...(displayOnly ? { share: null } : {})}
            />
          )}
        </div>
      </div>
      {collectionsExpanded && (
        <>
          {isMini() ? (
            <CollectionsMiniGrid
              collections={collections}
            />
          ) : (
            <>
              {collectionsView === 'grid' && (
                <CollectionsGrid
                  collections={collections}
                  handleMoveSelectedItems={handleMoveSelectedItems}
                  allowCheck={displayOnly ? canDownloadPageItem : true}
                  displayOnly={true}
                  renderDisplayAction={renderDisplayAction}
                  collectionUrlResolver={collectionUrlResolver}
                />
              )}
              {collectionsView === 'framed' && (
                <CollectionsGrid
                  collections={collections}
                  handleMoveSelectedItems={handleMoveSelectedItems}
                  selectedView="framed"
                  allowCheck={displayOnly ? canDownloadPageItem : true}
                  displayOnly={true}
                  renderDisplayAction={renderDisplayAction}
                  collectionUrlResolver={collectionUrlResolver}
                />
              )}
              {collectionsView === 'list' && (
                <CollectionsList
                  collections={collections}
                  handleMoveSelectedItems={handleMoveSelectedItems}
                  displayOnly={true}
                  renderDisplayAction={renderDisplayAction}
                  collectionUrlResolver={collectionUrlResolver}
                />
              )}
            </>
          )}
          {(!loadMoreCollections && !collectionsEnd) && (
            <div className="text-center">
              <Button
                onClick={handleLoadMoreCollections}
              >
                {intl.formatMessage({ defaultMessage: 'Load more collections' })}
              </Button>
            </div>
          )}
          {collectionsLoading && (
            <Loading />
          )}
          {(loadMoreCollections && !collectionsEnd) && (
            <Waypoint
              bottomOffset="200px"
              onEnter={loadCollections}
            />
          )}
        </>
      )}
      <CollectionAddModal />
      {!displayOnly && collectionMoveModal && (
        <CollectionsMoveModal collections={moveModalCollections} />
      )}
    </>
  );
};

const mapStateToProps = state => ({
  collectionGroupId: state.Collections.collectionGroupId,
  collection: state.Collections.collection,
  collections: state.Collections.collections,
  selectedCollections: state.Collections.selectedCollections,
  collectionMoveModal: state.Collections.moveModalOpen,
  moveModalCollection: state.Collections.moveModalCollection,
  collectionsPage: state.Collections.params.page,
  collectionsLoading: state.Collections.collectionsLoading,
  collectionsEnd: state.Collections.collectionsEnd,
  collectionsTotal: state.Collections.collectionsTotal,
  filesParams: state.Files.params,
  search: state.Search.search,
});

CollectionResultsSection.propTypes = {
  dispatch: PropTypes.func.isRequired,
  collection: PropTypes.object,
  collections: PropTypes.array.isRequired,
  selectedCollections: PropTypes.array.isRequired,
  collectionMoveModal: PropTypes.bool.isRequired,
  moveModalCollection: PropTypes.array.isRequired,
  collectionsEnd: PropTypes.bool.isRequired,
  collectionsTotal: PropTypes.number.isRequired,
  collectionsLoading: PropTypes.bool.isRequired,
  filesParams: PropTypes.object.isRequired,
  search: PropTypes.string,
  handleMoveSelectedItems: PropTypes.func.isRequired,
  onLoadMore: PropTypes.func,
  onRefresh: PropTypes.func,
  displayOnly: PropTypes.bool,
  hideSaveSort: PropTypes.bool,
  renderDisplayAction: PropTypes.func,
  collectionUrlResolver: PropTypes.func,
};

CollectionResultsSection.defaultProps = {
  onRefresh: null,
  displayOnly: false,
  hideSaveSort: false,
  renderDisplayAction: null,
  collectionUrlResolver: null,
  collection: null,
  search: null,
};

export default withRouter(connect(mapStateToProps)(CollectionResultsSection));
